import React from 'react';

import { useLocation, useNavigate } from 'react-router';

import { OverlayTrigger, Tooltip, Image } from "react-bootstrap";
import DynamicTable from '../widgets/dynamicTable/DynamicTable'
import HelperMetiers360 from '../../services/HelpersMetiers360';
import TooltipListComponent from '../widgets/tooltipList/TooltipListComponent';

const GroupsTableComponent = ({ groups, groupsSlugs }) => {
    
    const navigate = useNavigate();
    const location = useLocation();

    const groupsToDisplay = groups ? groups : [];  

    const getVideosContained = (group) => {
        if (group.contents?.length) {
            return group.contents.filter((c)=>c.type === 'video');
        } else {
            return [];
        }
    }

    const getQuizzesContained = (group) => {
        if (group.contents?.length) {
            return group.contents.filter((c)=>c.type === 'quiz');
        } else {
            return [];
        }
    }

    const getGroupsContained = (group) => {
        if (group.contents?.length) {
            return group.contents.filter((c)=>c.type === 'group');
        } else {
            return [];
        }
    }

    if (groups.length === 0 )
    {return <></>;}

    const sortType = [
        {
            value: 'thumbnail', label: 'Vignette',
            test: groupsToDisplay[0].links.thumbnail !== undefined,
            display: (group) => (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip>
                            <Image src={group.links.thumbnail}/>
                        </Tooltip>
                    }    
                >
                    <Image src={group.links.thumbnail} className="img-overlay" thumbnail />
                </OverlayTrigger>
            ),
            flatDisplay : (group) => group.links.thumbnail
        },
        {
            value: 'secondaryThumbnail', label: 'Vignette source',
            test: groupsToDisplay[0].links.secondaryThumbnail !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(!!a.links?.secondaryThumbnail, !!b.links?.secondaryThumbnail),
            display: (group) => group.links?.secondaryThumbnail ? <i className='fas fa-check' /> : <i className="fas fa-times" />,
            flatDisplay: (group) => group.links?.secondaryThumbnail ? "Oui" : "Non"
        },
        {
            value: 'name', label: "Nom",
            test : groupsToDisplay[0].name !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.name, b.name),
            display: (group) => group.name
        },
        {
            value: 'idHeadset', label: "idHeadset",
            test : groupsToDisplay[0].idHeadset !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.idHeadset, b.idHeadset),
            display: (group) => group.idHeadset
        },
        {
            value: 'videoContent', label: "Expériences immersives",
            test : groupsToDisplay[0].name !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getVideosContained(a)?.length, getVideosContained(b)?.length),
            display: (group) => {
                const videoList = getVideosContained(group);
                if (!videoList.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `group-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={videoList}
                    keyToDisplay='name'
                    generateIdCallback={generateIdCallback}
                 />

            },
            flatDisplay: (group) => getVideosContained(group).length
        },
        {
            value: 'quizContent', label: "Quiz",
            test : groupsToDisplay[0].name !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getQuizzesContained(a)?.length, getQuizzesContained(b)?.length),
            display: (group) => {
                const quizList = getQuizzesContained(group);
                if (!quizList.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `quiz-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={quizList}
                    keyToDisplay='name'
                    generateIdCallback={generateIdCallback}
                 />
            },
            flatDisplay: (group) => getQuizzesContained(group).length
        },
        {
            value: 'GroupContent', label: "Groupes",
            test : groupsToDisplay[0].name !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(getGroupsContained(a)?.length, getGroupsContained(b)?.length),
            display: (group) => {
                const groupList = getGroupsContained(group);
                if (!groupList.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `group-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={groupList}
                    keyToDisplay='name'
                    generateIdCallback={generateIdCallback}
                 />
            },
            flatDisplay: (group) => getGroupsContained(group).length
        },
        {
            value: 'parentGroups', label: "Parents",
            test : groupsToDisplay[0].name !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.groups?.length, b.groups?.length),
            display: (group) => {
                
                if (!group.groups || !group.groups.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `parent-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={group.groups}
                    keyToDisplay='name'
                    generateIdCallback={generateIdCallback}
                 />
            },
            flatDisplay: (group) => group?.groups?.length
        },
        {
            value: 'pedagogicModule', label: "Modules",
            test : groupsToDisplay[0].pedagogicModules !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.pedagogicModules?.length, b.pedagogicModules?.length),
            display: (group) => {
                
                if (!group.pedagogicModules || !group.pedagogicModules.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `pedagogicModule-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={group.pedagogicModules}
                    keyToDisplay='name'
                    generateIdCallback={generateIdCallback}
                 />
            },
            flatDisplay: (group) => group?.pedagogicModules?.length
        },
        {
            value: 'fileContent', label: "Fichiers",
            test : groupsToDisplay[0].files !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.files?.length, b.files?.length),
            display: (group) => {
                if (!group.files ||!group.files.length) {
                    return 0
                }

                const generateIdCallback = (uniqueId) => {
                    return `file-${uniqueId}`;
                }

                return <TooltipListComponent
                    inputList={group.files}
                    keyToDisplay='localName'
                    generateIdCallback={generateIdCallback}
                 />

            },
            flatDisplay: (group) => group.files.length
        },
        {
            value: 'isInteractive', label: "Interactif",
            test: HelperMetiers360.isArrayContainsValue(groups, "isInteractive"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.isInteractive, b.isInteractive),
            display: (group) => group.isInteractive ? 'X' : '-'
        },
        {
            value: 'position', label: "Position",
            test : groups.some(g => g.position),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.position, b.position),
            display: (group) => group.position ? group.position : '-'
        },
        {
            value: 'private', label: "Privée",
            test : groupsToDisplay[0].private !== undefined,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.private, b.private),
            display: (group) => group.private ? 'X' : '-'
        },
        {
            value: 'producers', label: "Producteur",
            test : !!groupsToDisplay[0].producers,
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.producersName, b.producersName),
            display: (group) => group.producersName ? group.producersName : '-'
        },
        {
            value: 'date', label: "Publication",
            test : groupsToDisplay[0].releaseDate !== undefined,
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.releaseDate, b.releaseDate),
            display: (group) => HelperMetiers360.getdisplayDateType(group.releaseDate, 'day')
        },
        {
            value: 'updatedAt', label: "Mise à jour",
            test : true,//groupsToDisplay[0].lastUpdated != undefined,
            method: (a, b) => HelperMetiers360.dateCompareWithNullableDescending(a.updatedAt, b.updatedAt),
            display: (group) => HelperMetiers360.getdisplayDateType(group.updatedAt, 'day')
        },
        {
            value: 'directAccessCode', label: "Code d'accès",
            test : HelperMetiers360.isArrayContainsValue(groupsToDisplay, "directAccessCode"),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.directAccessCode, b.directAccessCode),
            display: (group) => group.isDirectlyAccessible && group.directAccessCode
        },
    ];

    const onSelectGroup = (uniqueID) => {
         // eslint-disable-next-line no-unused-expressions
         groupsSlugs.readOne
             ? navigate(groupsSlugs.readOne.replace(':uniqueId', uniqueID), { state: { olderSearchParams: location.search } })
            : null;
    }

    return <DynamicTable 
        contentTable = {groupsToDisplay}
        contentSort = {sortType}
        valueInitSort = "idHeadset"
        index = 'uniqueId'
        handleClick = {onSelectGroup}
        withParams={true}
        tableName="groupsTable"
        filename="groupes"
    />;
}

export default GroupsTableComponent;